import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export const STATUS_LABELS: Record<string, string> = {
  new: 'Pending', investigating: 'Fixing', contained: 'Contained', closed: 'Fixed',
};

export const STATUS_BADGE: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  investigating: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
  contained: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  closed: 'bg-green-100 text-green-700 hover:bg-green-200',
};

const STATUS_DOT: Record<string, string> = {
  new: 'bg-blue-500', investigating: 'bg-amber-500', contained: 'bg-purple-500', closed: 'bg-green-500',
};

const OPTIONS = ['new', 'investigating', 'contained', 'closed'];

interface Props {
  value: string;
  onChange: (status: string) => void | Promise<void>;
  disabled?: boolean;
}

// Badge di stato cliccabile che apre un menu per cambiare lo stato senza aprire il dettaglio.
export default function StatusSelect({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function pick(status: string) {
    setOpen(false);
    if (status === value) return;
    setBusy(true);
    try {
      await onChange(status);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        disabled={disabled || busy}
        className={`badge inline-flex items-center gap-1 transition-colors ${STATUS_BADGE[value]} ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[value]}`} />
        {STATUS_LABELS[value]}
        {!disabled && <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 right-0 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
            {OPTIONS.map(o => (
              <button key={o} onClick={e => { e.stopPropagation(); pick(o); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-50">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[o]}`} />
                <span className="flex-1">{STATUS_LABELS[o]}</span>
                {o === value && <Check size={12} className="text-primary-600" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
